import { useEffect, useState } from 'react'
import {
  Loader2, Plus, Trash2, Pencil, ChevronDown, ChevronRight, FileText,
  AlertTriangle, Archive, Sparkles,
} from 'lucide-react'
import api from '../../services/api'
import { stagesFromDeliverables } from '../../utils/templateShape'
import { useAuth, userRoles } from '../../hooks/useAuth'

/**
 * The template library — the shapes a new project can start from.
 *
 * A template holds the SHAPE of a piece of work, never its contents: the stages it goes
 * through, and what to ask for. No dates, no money and no names, because those belong to a
 * project rather than to the kind of work it is.
 *
 * Ours are offered to everybody. A company's own are offered only to that company, which is
 * the same boundary the rest of the work is built around.
 */
// One library, two kinds of thing in it. Held here rather than in two screens, because they
// are the same object with the same permissions and the same reasons for existing.
const KINDS = [
  ['project', 'Starting a project', 'The shapes a new project can start from.'],
  ['closure', 'Closing one down', 'The shape of the note written when a project finishes.'],
  ['report', 'Writing it up', 'The headings a project report is written under.'],
]

export default function TemplateLibrary() {
  const { user } = useAuth()
  const [kind, setKind] = useState('project')
  const [items, setItems] = useState(null)
  const [error, setError] = useState(null)
  const [note, setNote] = useState(null)

  // The same rule the server enforces, so the screen does not offer a button that will be
  // refused. The server is still the one that decides.
  const canManage = user?.role === userRoles.ADMIN
    || user?.role === userRoles.PROJECT_MANAGER
    || (user?.capabilities || []).includes('manage_all_projects')

  const load = async () => {
    try {
      const { data } = await api.get(`/templates/?kind=${kind}`)
      setItems(data?.items || [])
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load the templates.')
      setItems([])
    }
  }
  useEffect(() => { setItems(null); setNote(null); load() }, [kind])

  if (items === null) {
    return <div className="p-6 flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
  }

  const after = async (message) => { setNote(message || null); setError(null); await load() }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
        <p className="text-sm text-gray-500 mt-1">
          {(KINDS.find(([k]) => k === kind) || [])[2]}
          {kind === 'project' && ' A template holds the stages a kind of work goes through — never its dates, its money or its people.'}
        </p>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {KINDS.map(([k, label]) => (
          <button key={k} onClick={() => setKind(k)}
            className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 transition ${kind === k
              ? 'border-indigo-600 text-indigo-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </p>
      )}
      {note && <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{note}</p>}

      {canManage && <Add kind={kind} after={after} setError={setError} />}

      {items.length === 0 ? (
        <p className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg px-4 py-6 text-center">
          No templates yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map(t => (
            <Template key={t.id} t={t} canManage={canManage} after={after} setError={setError} />
          ))}
        </ul>
      )}

      {!canManage && (
        <p className="text-xs text-gray-500">
          You can start a project from any of these. Writing and changing them is for Admins
          and Project Managers — retiring one takes it away from everybody at once.
        </p>
      )}
    </div>
  )
}

/* ── stages ───────────────────────────────────────────────────────────────── */

/**
 * WHY THE BUDGET TYPE IS WORKED OUT AND NOT ASKED.
 *
 * The research template shipped with stages that mixed a fixed fee and field work claimed on
 * receipts, and a budget type of "Actual" — under which the whole project is reimbursable, so
 * every stage rendered as claimed with bills and the mix was silently lost. Nobody spotted it
 * until a real project was started from it.
 *
 * It cannot be got wrong if it is not a separate question. The stages are the truth; the budget
 * type is a consequence of them.
 */
export function budgetTypeFor(stages) {
  const withBills = stages.filter(s => s.bills).length
  if (withBills === 0) return 'Fixed'                    // every stage a fixed fee
  if (withBills === stages.length) return 'Actual'       // everything on receipts
  return 'Hybrid'                                        // some of each
}

const BUDGET_EXPLANATION = {
  Fixed: 'Every stage is a fixed fee.',
  Actual: 'Every stage is claimed with bills.',
  Hybrid: 'A mix — some stages a fixed fee, some claimed with bills.',
}

/** Rows rather than one-per-line text, so a stage's payment is visible and editable. */
function StageEditor({ stages, setStages }) {
  const budget = budgetTypeFor(stages)
  return (
    <div className="space-y-2">
      <span className="block text-xs font-medium text-gray-700">Stages</span>
      {stages.map((st, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-xs text-gray-400 w-4 shrink-0">{i + 1}</span>
          <input
            value={st.title}
            onChange={e => setStages(stages.map((x, j) => j === i ? { ...x, title: e.target.value } : x))}
            placeholder="What happens at this stage"
            className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <label className="text-xs text-gray-600 inline-flex items-center gap-1 shrink-0" title="Paid against receipts rather than a fixed fee">
            <input type="checkbox" checked={!!st.bills}
                   onChange={e => setStages(stages.map((x, j) => j === i ? { ...x, bills: e.target.checked } : x))}
                   className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            with bills
          </label>
          <button type="button" onClick={() => setStages(stages.filter((_, j) => j !== i))}
                  className="text-gray-300 hover:text-red-600 shrink-0" title="Remove this stage">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button type="button" onClick={() => setStages([...stages, { title: '', bills: false }])}
              className="text-xs text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1">
        <Plus className="w-3.5 h-3.5" /> Add a stage
      </button>
      <p className="text-xs text-gray-500">
        No dates and no amounts — each project sets its own.
        {stages.length > 0 && <> Money: <strong>{budget}</strong> — {BUDGET_EXPLANATION[budget]}</>}
      </p>
    </div>
  )
}

/** What the server stores, from what the rows hold. */
function stagesToApi(stages) {
  return stages
    .filter(st => (st.title || '').trim())
    .map(st => ({ title: st.title.trim(), payment_mode: st.bills ? 'Actual' : 'Fixed' }))
}

/** And back again, for editing something that already exists. */
function stagesFromApi(milestones) {
  return (milestones || []).map(m => ({ title: m.title || '', bills: m.payment_mode === 'Actual' }))
}


/* ── sections, for a closure note ─────────────────────────────────────────── */

/**
 * A closure template is a list of headings, and they are kept in the same "fields" the project
 * kind uses for what it asks for. Same idea either way: the things somebody has to fill in.
 */
function sectionsToApi(list) {
  return list
    .map(x => (x || '').trim())
    .filter(Boolean)
    .map((label, n) => ({ key: `s${n}`, label, type: 'textarea', required: false, help: '' }))
}

function sectionsFromApi(fields) {
  return (fields || []).map(f => f.label || f.key || '')
}

function SectionEditor({ sections, setSections, label = 'Sections of the note' }) {
  return (
    <div className="space-y-2">
      <span className="block text-xs font-medium text-gray-700">{label}</span>
      {sections.map((text, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-xs text-gray-400 w-4 shrink-0">{i + 1}</span>
          <input
            value={text}
            onChange={e => setSections(sections.map((x, j) => j === i ? e.target.value : x))}
            placeholder="A question worth answering"
            className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button type="button" onClick={() => setSections(sections.filter((_, j) => j !== i))}
                  className="text-gray-300 hover:text-red-600 shrink-0" title="Remove">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button type="button" onClick={() => setSections([...sections, ''])}
              className="text-xs text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1">
        <Plus className="w-3.5 h-3.5" /> Add a section
      </button>
      <p className="text-xs text-gray-500">
        Headings only. What goes under them is written when the document is made.
      </p>
    </div>
  )
}


/* ── one template ─────────────────────────────────────────────────────────── */

function Template({ t, canManage, after, setError }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ name: t.name, description: t.description || '' })
  const [stages, setStages] = useState(stagesFromApi(t.milestones))
  const [sections, setSections] = useState(sectionsFromApi(t.fields))
  // Both kinds are a list of headings, held in the same place and edited the same way.
  const isClosure = t.kind === 'closure' || t.kind === 'report'

  const save = async (e) => {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      const body = { name: form.name.trim(), kind: t.kind,
                     description: form.description.trim() || null }
      if (isClosure) {
        body.fields = sectionsToApi(sections)
      } else {
        body.milestones = stagesToApi(stages)
        // Sent together, always. The budget type follows from the stages, so saving stages
        // without it is what let the two disagree in the first place.
        body.defaults = { ...(t.defaults || {}), budget_type: budgetTypeFor(stages) }
      }
      await api.put(`/templates/${t.id}`, body)
      setEditing(false)
      await after(`'${form.name.trim()}' saved.`)
    } catch (e) { setError(e.response?.data?.detail || 'Could not save that template.') }
    finally { setBusy(false) }
  }

  const cancel = () => {
    // Back to what is stored, not to whatever was half-typed.
    setForm({ name: t.name, description: t.description || '' })
    setStages(stagesFromApi(t.milestones))
    setSections(sectionsFromApi(t.fields))
    setEditing(false)
  }

  const retire = async () => {
    setBusy(true); setError(null)
    try {
      const { data } = await api.delete(`/templates/${t.id}`)
      setConfirming(false)
      await after(data?.detail)
    } catch (e) { setError(e.response?.data?.detail || 'Could not retire that template.') }
    finally { setBusy(false) }
  }

  return (
    <li className="border border-gray-200 rounded-lg">
      <div className="px-4 py-3 flex items-center gap-3">
        <button onClick={() => setOpen(o => !o)} className="text-gray-400 hover:text-gray-700 shrink-0">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <button onClick={() => setOpen(o => !o)} className="min-w-0 flex-1 text-left">
          <span className="block text-sm font-medium text-gray-900 truncate">{t.name}</span>
          <span className="block text-xs text-gray-500 truncate">
            {(t.kind === 'closure' || t.kind === 'report')
              ? <>{(t.fields || []).length} section{(t.fields || []).length === 1 ? '' : 's'}</>
              : <>{(t.milestones || []).length} stage{(t.milestones || []).length === 1 ? '' : 's'}</>}
            {/* Shown only when there are some. A hand-written template has none, and
                "0 fields" read as though something had failed to save — nothing had. What a
                template asks for is not collected when writing one, because nothing uses it
                yet: see the note in QUESTIONS-FOR-DECISION.md. */}
            {t.kind === 'project' && (t.fields || []).length > 0 &&
              <>{' · '}{t.fields.length} field{t.fields.length === 1 ? '' : 's'}</>}
            {t.organisation_id ? ' · one company only' : ' · offered to everybody'}
            {t.from_project ? ' · taken from a real project' : ''}
          </span>
        </button>
        {canManage && !confirming && !editing && (
          <>
            <button onClick={() => setEditing(true)} disabled={busy}
                    className="text-gray-300 hover:text-indigo-700 disabled:opacity-40" title="Edit">
              <Pencil className="w-4 h-4" />
            </button>
            <button onClick={() => setConfirming(true)} disabled={busy}
                    className="text-gray-300 hover:text-red-600 disabled:opacity-40" title="Retire">
              <Archive className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {confirming && (
        <div className="px-4 pb-3 space-y-2">
          <p className="text-xs text-gray-600">
            Retiring stops it being offered. Nothing already created from it changes — projects
            keep their own copy of the shape, so retiring is not deleting.
          </p>
          <div className="flex gap-2">
            <button onClick={retire} disabled={busy}
                    className="text-sm font-medium rounded-lg px-3 py-1.5 bg-white text-red-700 border border-red-200 hover:bg-red-50 inline-flex items-center gap-1.5">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Retire it
            </button>
            <button onClick={() => setConfirming(false)}
                    className="text-sm rounded-lg px-3 py-1.5 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {editing && (
        <form onSubmit={save} className="px-4 pb-4 pt-1 border-t border-gray-100 space-y-3 bg-gray-50">
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Name</span>
            <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                   className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">What it is for</span>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                   className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </label>
          {isClosure
            ? <SectionEditor sections={sections} setSections={setSections}
                             label={t.kind === 'report' ? 'Sections of the report' : 'Sections of the note'} />
            : <StageEditor stages={stages} setStages={setStages} />}
          <p className="text-xs text-gray-500">
            Changes apply to projects started from here <strong>afterwards</strong>. Anything
            already created keeps the shape it was given.
          </p>
          <div className="flex gap-2">
            <button type="submit" disabled={busy}
                    className="text-sm font-medium rounded-lg px-3 py-2 bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-indigo-300 inline-flex items-center gap-1.5">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
            </button>
            <button type="button" onClick={cancel}
                    className="text-sm rounded-lg px-3 py-2 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      )}

      {open && !editing && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100 space-y-3">
          {t.description && <p className="text-xs text-gray-600">{t.description}</p>}

          {(t.kind === 'closure' || t.kind === 'report') ? (
            <div>
              <p className="text-xs font-medium text-gray-700 mb-1">
                {t.kind === 'report' ? 'Sections of the report' : 'Sections of the note'}
              </p>
              <ol className="text-xs text-gray-700 space-y-1">
                {sectionsFromApi(t.fields).map((label, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full bg-gray-100 text-gray-500 text-[10px] flex items-center justify-center shrink-0">{i + 1}</span>
                    <span>{label}</span>
                  </li>
                ))}
              </ol>
            </div>
          ) : (
          <div>
            <p className="text-xs font-medium text-gray-700 mb-1">Stages</p>
            {(t.milestones || []).length === 0 ? (
              <p className="text-xs text-gray-400">None — the project decides its own.</p>
            ) : (
              <ol className="text-xs text-gray-700 space-y-1">
                {t.milestones.map((m, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full bg-gray-100 text-gray-500 text-[10px] flex items-center justify-center shrink-0">{i + 1}</span>
                    <span>{m.title}</span>
                    {m.payment_mode === 'Actual' && (
                      <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">claimed with bills</span>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>
          )}

          {t.kind === 'project' && (t.fields || []).length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-700 mb-1">What it asks for</p>
              <div className="flex flex-wrap gap-1">
                {t.fields.map((f, i) => (
                  <span key={i} className="text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">
                    {f.label || f.key}{f.required ? ' *' : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  )
}

/* ── adding one ───────────────────────────────────────────────────────────── */

/**
 * Two ways in, because they are genuinely different acts.
 *
 * Writing one down is a guess about what will be needed. Taking one from a project that worked
 * is a record of what actually was — which is why the starting four are few and the rest should
 * come from real work.
 */
function Add({ kind, after, setError }) {
  const isClosure = kind === 'closure' || kind === 'report'
  const [mode, setMode] = useState(null)   // null | 'blank' | 'project'
  const [busy, setBusy] = useState(false)

  const [form, setForm] = useState({ name: '', description: '' })
  const [stages, setStages] = useState([{ title: '', bills: false }])
  const [sections, setSections] = useState([''])
  const [projects, setProjects] = useState([])
  // FROM A DOCUMENT THEY ALREADY HAVE.
  //
  // A client's Terms of Reference carries their house format: the headings they use and the
  // stages they ask for. This is the route somebody takes when they are thinking about
  // templates — the same thing is offered inside the project form, but only in the moment a
  // document happens to be read, and a moment is easy to miss.
  const [docFile, setDocFile] = useState(null)
  const [docName, setDocName] = useState('')
  const [pick, setPick] = useState({ project_id: '', name: '' })

  useEffect(() => {
    if (mode !== 'project') return
    api.get('/projects/')
      .then(r => setProjects(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setProjects([]))
  }, [mode])

  const createBlank = async (e) => {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      // The SAME editors the edit form uses. Two ways of entering this would drift, and the
      // first thing to drift would be the payment mode, which is exactly what went wrong once.
      const body = { name: form.name.trim(), kind,
                     description: form.description.trim() || null }
      if (isClosure) {
        body.fields = sectionsToApi(sections)
      } else {
        body.milestones = stagesToApi(stages)
        body.defaults = { budget_type: budgetTypeFor(stages) }
      }
      const { data } = await api.post('/templates/', body)
      setForm({ name: '', description: '' })
      setStages([{ title: '', bills: false }])
      setSections([''])
      setMode(null)
      await after(`'${data?.name || form.name}' added.`)
    } catch (e) { setError(e.response?.data?.detail || 'Could not add that template.') }
    finally { setBusy(false) }
  }

  const createFromDocument = async (e) => {
    e.preventDefault()
    if (!docFile) return
    setBusy(true); setError(null)
    try {
      // The SAME reader the project form uses, so a document produces the same shape whichever
      // door it comes through. A second parser here would drift from that one immediately.
      const fd = new FormData()
      fd.append('file', docFile)
      const { data: parsed } = await api.post('/projects/from-tor', fd)

      // Only real headings. The AI reader hands the whole document back under one key, which
      // describes no format at all, so it is left out rather than saved as one.
      const sections = Object.keys(parsed.raw_sections || {})
        .filter(k => k && k !== 'ai_full_text')
        .map(k => k.trim())
        .filter(k => k.length > 2 && k.length < 80)

      const name = docName.trim() || parsed.proposed_name || docFile.name.replace(/\.[^.]+$/, '')
      const { data } = await api.post('/templates/', {
        name,
        kind: 'project',
        description: 'Taken from a Terms of Reference document.',
        // Stages only — never the dates or the money any one project put against them.
        // Deadlines taken off the front: a Terms of Reference writes its deliverables as
        // "By 31 Aug 2026, submit…", and kept verbatim the template carries that project's
        // dates into the next one.
        milestones: stagesFromDeliverables(parsed.deliverables),
        fields: sections.map((label, n) => ({
          key: `s${n}`, label, type: 'textarea', required: false, help: '',
        })),
      })
      setDocFile(null); setDocName(''); setMode(null)
      await after(`'${data?.name || name}' added — ${(data?.milestones || []).length} stage(s)`
        + `${sections.length ? `, ${sections.length} section(s)` : ' and no headings this reader could see'}.`)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not read that document.')
    } finally { setBusy(false) }
  }

  const createFromProject = async (e) => {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      const q = pick.name.trim() ? `?name=${encodeURIComponent(pick.name.trim())}` : ''
      const { data } = await api.post(`/templates/from-project/${pick.project_id}${q}`)
      setPick({ project_id: '', name: '' })
      setMode(null)
      await after(`'${data?.name}' added — its stages, not its contents.`)
    } catch (e) { setError(e.response?.data?.detail || 'Could not make a template from that project.') }
    finally { setBusy(false) }
  }

  if (!mode) {
    return (
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setMode('blank')}
                className="text-sm font-medium rounded-lg px-3 py-2 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Write one
        </button>
        {/* Only for the project kind. A closure note has no stages to copy off a project,
            so offering it would lead somewhere that could not work. */}
        {!isClosure && (
          <button onClick={() => setMode('document')}
                  className="text-sm font-medium rounded-lg px-3 py-2 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-1.5">
            <FileText className="w-4 h-4" /> Take one from a document
          </button>
        )}
        {!isClosure && (
          <button onClick={() => setMode('project')}
                  className="text-sm font-medium rounded-lg px-3 py-2 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-1.5">
            <Sparkles className="w-4 h-4" /> Take one from a project that worked
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      {mode === 'document' ? (
        <form onSubmit={createFromDocument} className="space-y-3">
          <p className="text-xs text-gray-600 flex items-start gap-2">
            <FileText className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
            <span>
              Upload a client's Terms of Reference and we keep its shape — the headings it uses
              and the stages it asks for, with the deadlines taken off. No dates, no money. The
              wording stays as the document wrote it, and every stage can be edited afterwards.
            </span>
          </p>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">The document</span>
            <input type="file" accept=".docx,.pdf" required
                   onChange={e => setDocFile(e.target.files?.[0] || null)}
                   className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-gray-100" />
            <span className="block text-xs text-gray-500 mt-1">
              .docx or .pdf. A Word file keeps its headings; a PDF usually does not.
            </span>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Call it</span>
            <input value={docName} onChange={e => setDocName(e.target.value)}
                   placeholder="e.g. WHO — standard consultancy"
                   className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            <span className="block text-xs text-gray-500 mt-1">
              Left blank, it is named after the document.
            </span>
          </label>
          <div className="flex gap-2">
            <button type="submit" disabled={busy || !docFile}
                    className="text-sm font-medium rounded-lg px-3 py-2 bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-indigo-300 inline-flex items-center gap-1.5">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Read it and save the shape
            </button>
            <button type="button" onClick={() => { setMode(null); setDocFile(null) }}
                    className="text-sm rounded-lg px-3 py-2 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      ) : mode === 'blank' ? (
        <form onSubmit={createBlank} className="space-y-3">
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Name</span>
            <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                   placeholder={kind === 'report' ? 'Progress report - funder'
                     : isClosure ? 'Closure note - evaluation' : 'Terms of Reference - evaluation'}
                   className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">What it is for</span>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                   placeholder={isClosure ? 'When to use this note rather than another' : 'When to reach for this one rather than another'}
                   className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </label>
          {isClosure
            ? <SectionEditor sections={sections} setSections={setSections}
                             label={kind === 'report' ? 'Sections of the report' : 'Sections of the note'} />
            : <StageEditor stages={stages} setStages={setStages} />}
          <div className="flex gap-2">
            <button type="submit" disabled={busy}
                    className="text-sm font-medium rounded-lg px-3 py-2 bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-indigo-300 inline-flex items-center gap-1.5">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Add
            </button>
            <button type="button" onClick={() => setMode(null)}
                    className="text-sm rounded-lg px-3 py-2 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={createFromProject} className="space-y-3">
          <p className="text-xs text-gray-600 flex items-start gap-2">
            <FileText className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
            <span>
              Copies the project's stages and nothing else. None of its dates, money, documents
              or people come across — a template records the shape of work that went well, not
              the work itself.
            </span>
          </p>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Which project</span>
            <select required value={pick.project_id}
                    onChange={e => setPick({ ...pick, project_id: e.target.value })}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500">
              <option value="">Choose a project…</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>
                  {p.project_number ? `${p.project_number} — ` : ''}{p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Call it</span>
            <input value={pick.name} onChange={e => setPick({ ...pick, name: e.target.value })}
                   placeholder="Left blank, it is named after the project"
                   className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </label>
          <div className="flex gap-2">
            <button type="submit" disabled={busy || !pick.project_id}
                    className="text-sm font-medium rounded-lg px-3 py-2 bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-indigo-300 inline-flex items-center gap-1.5">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Make the template
            </button>
            <button type="button" onClick={() => setMode(null)}
                    className="text-sm rounded-lg px-3 py-2 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
